import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Tooltip
} from '@mui/material';
import { CurrencyRupee, AttachMoney as PricingIcon, ContentCopy } from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { api } from '../../../../services/api';
import { TripPricingSectionProps, Transaction, PaymentLinkResponse } from '../../../../types/tripPricingSection';
import * as yup from 'yup';
import { fetchDrivers } from '../../../../store/actions';



const cashPaymentValidationSchema = yup.object().shape({
  paymentMode: yup.string().required('Payment mode is required'),
  refNumber: yup.string().when('paymentMode', {
    is: (value: string) => value === 'QRCODE' || value === 'DRQRCODE',
    then: (schema) => schema.trim()
      .required('Receipt / Transaction number is required for QR payments'),
    otherwise: (schema) => schema.notRequired()
  }),
  driverId: yup.number().nullable().when('paymentMode', {
    is: 'CASH',
    then: (schema) => schema.required('Driver selection is required for cash payments').typeError('Driver selection is required for cash payments'),
    otherwise: (schema) => schema.notRequired()
  })
});

// Add this near the top with the other validation schema
const refundValidationSchema = yup.object().shape({
  refundAmount: yup
    .number()
    .typeError('Refund amount must be a valid number')
    .required('Refund amount is required')
    .positive('Refund amount must be greater than 0')
    .test('max-refund', 'Refund amount cannot exceed total paid amount', function (value) {
      const { totalPaid } = this.options.context || {};
      return value ? value <= totalPaid : false;
    })
    .test('total-refund-check', 'Refund amount cannot exceed total paid amount', function (value) {
      const { cancellationFee } = this.parent;
      const { totalPaid } = this.options.context || {};
      const cancellationAmount = cancellationFee ? parseFloat(cancellationFee) : 0;
      const total = (value || 0) + cancellationAmount;
      return totalPaid ? total <= totalPaid : true;
    }),
  refundReason: yup
    .string()
    .required('Please select a reason for refund'),
  refundTransactionId: yup
    .string()
    .required('Transaction ID is required')
});


const TripPricingSection: React.FC<TripPricingSectionProps> = ({
  pricing,
  extraCharges = [],
  loading = false,
  tripId = '',
  refreshTripDetails,
  customerName: initialCustomerName = '',
  customerMobile: initialCustomerMobile = '',
  customerEmail: initialCustomerEmail = '',
  disabled = false,
  customer,
  isInCancellation,
  onPaymentCalculated,
  driverDetails
}) => {
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [openPaymentLinkDialog, setOpenPaymentLinkDialog] = useState(false);
  const [openCashPaymentDialog, setOpenCashPaymentDialog] = useState(false);
  const [openWhatsAppDialog, setOpenWhatsAppDialog] = useState<boolean>(false);
  const [openPaymentLinkWhatsAppDialog, setOpenPaymentLinkWhatsAppDialog] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [selectedPaymentTransaction, setSelectedPaymentTransaction] = useState<Transaction | null>(null);
  const [selectedPaymentLinkTransaction, setSelectedPaymentLinkTransaction] = useState<Transaction | null>(null);
  const [sendingNotification, setSendingNotification] = useState<boolean>(false);
  const [paymentLink, setPaymentLink] = useState<string>('');
  const [paymentTransaction, setPaymentTransaction] = useState<PaymentLinkResponse['data'] | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [customerName, setCustomerName] = useState<string>('');
  const [customerMobile, setCustomerMobile] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [refNumber, setRefNumber] = useState<string>('');
  const [paymentNotes, setPaymentNotes] = useState<string>('');
  const [paymentMode, setPaymentMode] = useState<string>('CASH');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);

  // Add ref for scroll position preservation
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);
  const [openCashHandoverDialog, setOpenCashHandoverDialog] = useState(false);
  const [selectedCashTransactionId, setSelectedCashTransactionId] = useState<number | null>(null);
  // const [cashInHandTo, setCashInHandTo] = useState<string>('OPERATOR');
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    refNumber?: string;
    paymentMode?: string;
    driverId?: string;
  }>({});

  const [refundValidationErrors, setRefundValidationErrors] = useState<{
    refundAmount?: string;
    cancellationFee?: string;
    refundReason?: string;
    refundTransactionId?: string;
  }>({});

  const [openModifyAmountDialog, setOpenModifyAmountDialog] = useState(false);
  const [modifyAmount, setModifyAmount] = useState<string>('');
  const [modifyAmountError, setModifyAmountError] = useState<string>('');
  const [selectedModifyTransaction, setSelectedModifyTransaction] = useState<Transaction | null>(null);

  const [refundAmount, setRefundAmount] = useState<string>('');
  const [refundAmountError, setRefundAmountError] = useState<string>('');
  const [refundReason, setRefundReason] = useState<string>('');
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [cancellationFee, setCancellationFee] = useState<string>('');
  const [refundTransactionId, setRefundTransactionId] = useState<string>('');


  const dispatch = useDispatch();
  const userId = useSelector((state: any) => state.auth.user?.userId);
  const userRole = useSelector((state: any) => state.auth.user?.role);
  const { items: drivers = [], loading: driversLoading } = useSelector((state: any) => state.drivers);

  const cashTransactionDate = new Date().toISOString();

  useEffect(() => {
    dispatch(fetchDrivers() as any);
  }, [dispatch])

  useEffect(() => {
    if (driverDetails && driverDetails.id) {
      setSelectedDriverId(driverDetails.id);
    }
  }, [driverDetails])

  // Fetch transactions when component mounts or after payment operations
  const fetchTransactions = async () => {
    if (!tripId) return;

    setLoadingTransactions(true);
    try {
      const response = await api.getTripTransactions(tripId);
      // Check if response exists and has the expected structure
      if (response && Array.isArray(response)) {
        setTransactions(response);
      } else if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
        setTransactions(response.data);
      } else {
        console.error('Unexpected response format:', response);
        setTransactions([]);
      }
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      setSnackbarMessage('Failed to fetch transaction history');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    if (tripId) {
      fetchTransactions();
    }
  }, [tripId]);

  // Simple scroll preservation after transactions update
  useEffect(() => {
    if (shouldPreserveScroll && paymentSectionRef.current) {
      setTimeout(() => {
        paymentSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setShouldPreserveScroll(false);
      }, 200);
    }
  }, [transactions, shouldPreserveScroll]);

  const handleCashHandoverDialog = (transactionId: number) => {
    setSelectedCashTransactionId(transactionId);
    setOpenCashHandoverDialog(true);
  }

  const SectionHeader = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#464545',
        color: 'white',
        p: 2,
        borderRadius: '8px 8px 0 0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CurrencyRupee />
        <Typography variant="h6" fontWeight="bold">
          Payment
        </Typography>
      </Box>
    </Box>
  );

  // Calculate total extra charges - use memoization to avoid unnecessary recalculations
  const totalExtraCharges = React.useMemo(() => {

    return Array.isArray(extraCharges) ? extraCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0) : 0;
  }, [extraCharges]);

  // Add extra charges to expected amount
  const expectedAmount = React.useMemo(() => {
    return (pricing?.pricing.agreedTripFare || 0) + totalExtraCharges;
  }, [pricing?.pricing.agreedTripFare, totalExtraCharges]);

  // Calculate total paid amount by summing up all successful payment transactions
  const calculateTotalPaid = () => {
    let paidAmount = 0;
    transactions.forEach(transaction => {
      // Only include transactions with status SUCCESS,
      if (transaction.status === 'SUCCESS') {
        paidAmount += transaction.amount;
      }
    });
    return paidAmount;
  };

  const totalPaid = transactions.length >= 0 ? calculateTotalPaid() : (pricing?.paidAmount || 0);
  const amountDue = expectedAmount - totalPaid;

  // Set payment amount to amount due when it changes, only if amount due is greater than zero
  useEffect(() => {
    if (amountDue > 0) {
      setPaymentAmount(amountDue.toString());
    } else {
      // Clear payment amount if due amount is zero
      setPaymentAmount('');
    }
  }, [amountDue]);

  // Initialize customer information from props
  useEffect(() => {
    setCustomerName(initialCustomerName);
    setCustomerMobile(initialCustomerMobile);
    setCustomerEmail(initialCustomerEmail);
  }, [initialCustomerName, initialCustomerMobile, initialCustomerEmail]);

  useEffect(() => {
    if (onPaymentCalculated) {
      onPaymentCalculated(totalPaid, amountDue);
    }
  }, [totalPaid, amountDue, onPaymentCalculated]);

  // Handle opening the payment link dialog
  const handleOpenPaymentLinkDialog = () => {
    // Pre-fill customer information if available
    setCustomerName(initialCustomerName);
    setCustomerMobile(initialCustomerMobile);
    setCustomerEmail(initialCustomerEmail);
    setOpenPaymentLinkDialog(true);
  };

  // Handle closing the payment link dialog
  const handleClosePaymentLinkDialog = () => {
    setOpenPaymentLinkDialog(false);
    setPaymentLink('');
  };

  // Handle opening the cash payment dialog
  const handleOpenCashPaymentDialog = (mode: 'CASH' | 'QRCODE' | 'DRQRCODE') => {
    setCustomerName(initialCustomerName);
    setCustomerMobile(initialCustomerMobile);
    setRefNumber('');
    setPaymentNotes('');
    setPaymentMode(mode); // Reset to default
    setValidationErrors({}); // Clear validation errors
    if (driverDetails && driverDetails.id) {
      setSelectedDriverId(driverDetails.id);
    } else {
      setSelectedDriverId(null);
    }
    setOpenCashPaymentDialog(true);
  };

  const handleSetCashToUser = async (cashInHandTo: 'OPERATOR' | 'ADMIN') => {
    if (selectedCashTransactionId) {
      try {
        const trnDate = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
        await api.setCashToUser(selectedCashTransactionId, cashInHandTo, trnDate);
        setSnackbarMessage(`Transaction marked as Cash to ${cashInHandTo === 'ADMIN' ? 'Admin' : 'Operator'} successfully!`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setOpenCashHandoverDialog(false);
        setSelectedCashTransactionId(null);
        // Refresh trip details and transactions to show updated payment status
        await fetchTransactions();
        if (refreshTripDetails) {
          await refreshTripDetails(); // THIS IS THE KEY - it refreshes the parent component
        }

      } catch (error: any) {
        console.error('Error marking Cash to User:', error);
        setSnackbarMessage(error.message || 'Failed to mark Cash to User');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setOpenCashHandoverDialog(false);
        setSelectedCashTransactionId(null);
      }
    }
  }

  // Handle closing the cash payment dialog
  const handleCloseCashPaymentDialog = () => {
    setOpenCashPaymentDialog(false);
    setValidationErrors({}); // Clear validation errors
  };

  // Handle creating a payment link
  const handleCreatePaymentLink = async () => {
    if (!tripId || !paymentAmount) return;

    setIsProcessing(true);
    setShouldPreserveScroll(true); // Set flag to preserve scroll
    try {
      // Use the amount exactly as entered by the user
      const amount = parseFloat(paymentAmount);

      const paymentLinkData = {
        amount,
        currency: 'INR',
        customerName,
        customerMobile,
        customerEmail: customerEmail || undefined,
        customerCountryCode: '91',
        description: `Payment for trip ${tripId}`,
        validityInMinutes: 1440 // 24 hours
      };

      const response = await api.createPaymentLink(tripId, paymentLinkData) as PaymentLinkResponse;

      if (response && response.status && response.data) {
        setPaymentLink(response.data.shortUrl);
        setPaymentTransaction(response.data);
        setSnackbarMessage('Payment link created successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setOpenPaymentLinkDialog(false); // Close the dialog

        // Refresh trip details and transactions to show updated payment status
        if (refreshTripDetails) {
          await refreshTripDetails();
        }
        await fetchTransactions();
      }
    } catch (error: any) {
      console.error('Error creating payment link:', error);
      setSnackbarMessage(error.message || 'Failed to create payment link');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setIsProcessing(false);
    }
  };
  const handleOminiRefund = async () => {
    if (!tripId) return;

    try {
      await refundValidationSchema.validate({
        refundAmount: parseFloat(refundAmount),
        cancellationFee: parseFloat(cancellationFee),
        refundReason,
        refundTransactionId
      }, {
        abortEarly: false,
        context: { totalPaid } // Pass totalPaid for validation
      });

      // Clear validation errors if validation passes
      setRefundValidationErrors({});
      setIsProcessing(true);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        // Map validation errors to individual fields
        const errors = error.inner.reduce((acc, err) => {
          if (err.path) {
            acc[err.path as keyof typeof refundValidationErrors] = err.message;
          }
          return acc;
        }, {} as typeof refundValidationErrors);

        setRefundValidationErrors(errors);
        return;
      }
    }
    const refundData = {
      tripCode: tripId,
      customerName: customerName,
      customerMobile: customerMobile,
      customerCountryCode: customer?.countryCode || '91',
      refundAmount: parseFloat(refundAmount),
      cancellationFee: cancellationFee ? parseFloat(cancellationFee) : 0,
      refundTransactionId: refundTransactionId,
      reason: refundReason || null,


    }
    try {
      await api.tripRefund(refundData);
      setSnackbarMessage('Refund processed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenRefundDialog(false);
      setRefundAmount('');
      setCancellationFee('0');
      setRefundReason('');
      setRefundTransactionId('');
      setRefundValidationErrors({});

      // Refresh transactions
      if (refreshTripDetails) {
        await refreshTripDetails();
      }
      await fetchTransactions();

    } catch (error: any) {
      console.error('Error processing OminiCard refund:', error);
      setSnackbarMessage(error.message || 'Failed to process refund');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCashPayment = async () => {
    if (!tripId || !paymentAmount) return;

    // Validate form data
    try {
      await cashPaymentValidationSchema.validate({
        paymentMode,
        refNumber,
        customerName,
        customerMobile,
        paymentNotes,
        driverId: selectedDriverId
      }, { abortEarly: false });

      // Clear any previous validation errors
      setValidationErrors({});

    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setValidationErrors(error.inner.reduce((acc, err) => (err.path ? { ...acc, [err.path]: err.message } : acc), {}));
        return;
      }
    }

    setIsProcessing(true);
    setShouldPreserveScroll(true); // Set flag to preserve scroll
    try {
      // Use the amount exactly as entered by the user
      const amount = parseFloat(paymentAmount);

      const cashPaymentData: any = {
        amount,
        paymentMode, // Include payment mode in payload
        notes: paymentNotes || null,
        customerName: customerName || undefined,
        customerMobile: customerMobile || undefined,
        refNumber: refNumber || undefined,
        cashTrnDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss")
      };

      if (paymentMode === 'CASH' && selectedDriverId) {
        cashPaymentData.cashUserId = selectedDriverId;
      }

      await api.processCashPayment(tripId, cashPaymentData);

      setSnackbarMessage('Cash payment processed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenCashPaymentDialog(false);
      setPaymentAmount('');
      setValidationErrors({}); // Clear validation errors on success

      // Refresh trip details and transactions to show updated payment status
      if (refreshTripDetails) {
        await refreshTripDetails();
      }
      await fetchTransactions();
    } catch (error: any) {
      console.error('Error processing cash payment:', error);
      setSnackbarMessage(error.message || 'Failed to process cash payment');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle copying payment link to clipboard
  const handleCopyPaymentLink = () => {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink);
      setSnackbarMessage('Payment link copied to clipboard!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
  };

  // Handle checking payment link status
  const handleCheckPaymentStatus = async (transaction: Transaction) => {
    if (!tripId || !transaction.razorpayPaymentLinkId) return;

    setIsProcessing(true);
    setShouldPreserveScroll(true); // Set flag to preserve scroll
    try {
      const response = await api.checkPaymentLinkStatus(tripId, transaction.razorpayPaymentLinkId);

      // Check if response exists and has the expected structure
      if (response && typeof response === 'object') {
        setSnackbarMessage('Payment status updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Refresh trip details and transactions to show updated payment status
        if (refreshTripDetails) {
          await refreshTripDetails();
        }
        await fetchTransactions();
      }
    } catch (error: any) {
      console.error('Error checking payment link status:', error);
      setSnackbarMessage(error.message || 'Failed to check payment status');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle opening the WhatsApp notification dialog
  const handleOpenWhatsAppDialog = (transaction: Transaction) => {
    setSelectedPaymentTransaction(transaction);
    setOpenWhatsAppDialog(true);
  };

  // Handle closing the WhatsApp notification dialog
  const handleCloseWhatsAppDialog = () => {
    setOpenWhatsAppDialog(false);
    setSelectedPaymentTransaction(null);
  };

  // Handle sending WhatsApp notification
  const handleSendWhatsAppNotification = async () => {
    if (!selectedPaymentTransaction || !initialCustomerMobile) return;

    setSendingNotification(true);
    try {
      // const mobileNumber = initialCustomerMobile.replace(/\D/g, '');
      let mobileNumber = initialCustomerMobile.replace(/\D/g, '');
      let countryCode = customer?.countryCode || '91';
      mobileNumber = mobileNumber.startsWith(countryCode)
        ? mobileNumber.slice(countryCode.length)
        : mobileNumber;

      const notificationData = {
        templateName: 'payment_received_confirmation',
        customerCountryCode: countryCode,
        customerNumber: mobileNumber,
        body: {
          name: initialCustomerName || 'Customer',
          amount: selectedPaymentTransaction.amount.toString(),
          tripcode: tripId || '',
          date: new Date(selectedPaymentTransaction.transactionDate).toLocaleDateString() || ''
        }
      };

      await api.sendWhatsAppNotification(notificationData);

      setSnackbarMessage('WhatsApp payment confirmation sent successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenWhatsAppDialog(false);
    } catch (error: any) {
      console.error('Error sending WhatsApp notification:', error);
      setSnackbarMessage(error.message || 'Failed to send WhatsApp notification');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSendingNotification(false);
    }
  };

  // Handle opening the payment link WhatsApp notification dialog
  const handleOpenPaymentLinkWhatsAppDialog = (transaction: Transaction) => {
    setSelectedPaymentLinkTransaction(transaction);
    setOpenPaymentLinkWhatsAppDialog(true);
  };

  // Handle closing the payment link WhatsApp notification dialog
  const handleClosePaymentLinkWhatsAppDialog = () => {
    setOpenPaymentLinkWhatsAppDialog(false);
    setSelectedPaymentLinkTransaction(null);
  };

  // Handle sending payment link WhatsApp notification
  const handleSendPaymentLinkWhatsAppNotification = async () => {
    if (!selectedPaymentLinkTransaction || !initialCustomerMobile) return;

    setSendingNotification(true);
    try {
      // const mobileNumber = initialCustomerMobile.replace(/\D/g, '');
      let mobileNumber = initialCustomerMobile.replace(/\D/g, '');
      let countryCode = customer?.countryCode || '91';
      mobileNumber = mobileNumber.startsWith(countryCode)
        ? mobileNumber.slice(countryCode.length)
        : mobileNumber;

      const notificationData = {
        templateName: 'payment_link_notification',
        customerCountryCode: countryCode,
        customerNumber: mobileNumber,
        body: {
          name: initialCustomerName || 'Customer',
          paylink: selectedPaymentLinkTransaction.razorpayPaymentLinkUrl || '',
          amount: selectedPaymentLinkTransaction.amount.toString(),
          tripcode: tripId || ''
        }
      };

      await api.sendWhatsAppNotification(notificationData);

      setSnackbarMessage('Payment link sent via WhatsApp successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenPaymentLinkWhatsAppDialog(false);
    } catch (error: any) {
      console.error('Error sending WhatsApp payment link notification:', error);
      setSnackbarMessage(error.message || 'Failed to send WhatsApp payment link notification');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSendingNotification(false);
    }
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  // Validation function for modify amount input
  const validateModifyAmountInput = (value: string): boolean => {
    if (value === '') return true;
    if (value.startsWith('-')) return false;
    return /^\d*\.?\d*$/.test(value);
  };
  const handleOpenModifyAmountDialog = (transaction: Transaction) => {
    setSelectedModifyTransaction(transaction);
    setModifyAmount(transaction.amount.toString());
    setModifyAmountError('');
    setOpenModifyAmountDialog(true);
  };

  const handleCloseModifyAmountDialog = () => {
    setOpenModifyAmountDialog(false);
    setSelectedModifyTransaction(null);
    setModifyAmount('');
    setModifyAmountError('');
  };

  const handleSaveModifyAmount = async () => {
    if (!selectedModifyTransaction || !tripId) return;
    const newAmount = parseFloat(modifyAmount);

    try {
      await api.modifyCashAmount(selectedModifyTransaction.id, newAmount);
      handleCloseModifyAmountDialog();
      if (refreshTripDetails) {
        await refreshTripDetails();
      }
      await fetchTransactions();
    } catch (error: any) {
      console.error('Error modifying amount:', error);
      setSnackbarMessage(error.message || 'Failed to modify amount');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }

  return (
    <Card ref={paymentSectionRef} sx={{ mb: 2, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
      <SectionHeader />
      <CardContent sx={{ p: 3, backgroundColor: '#161616', color: 'white' }}>
        {/* Payment Summary */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{
            background: '#f8fafc',
            borderRadius: 2,
            boxShadow: '0 2px 8px #0001',
            mx: 'auto',
            p: 0,
            overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #e5e7eb' }}>
                <Typography sx={{ color: '#334155', fontWeight: 500 }}>Base Fare</Typography>
                <Typography sx={{ color: '#334155', fontWeight: 500 }}>₹{(pricing?.pricing.agreedTripFare ?? 0).toFixed(2)}</Typography>
              </Box>
              {extraCharges.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {extraCharges.map((charge) => (
                    <Box key={charge.id || charge.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography sx={{ color: '#64748b' }}>
                        {charge.chargeType || charge.label || 'Extra Charge'}
                      </Typography>
                      <Typography sx={{ color: '#64748b' }}>
                        ₹{charge.amount.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderTop: '1px solid #e5e7eb', mt: 2 }}>
                <Typography sx={{ color: '#334155', fontWeight: 600 }}>Total Amount</Typography>
                <Typography sx={{ color: '#334155', fontWeight: 600 }}>
                  ₹{expectedAmount.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography sx={{ color: '#10b981', fontWeight: 500 }}>Total Paid</Typography>
                <Typography sx={{ color: '#10b981', fontWeight: 500 }}>
                  ₹{totalPaid.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, background: '#fee2e2', borderRadius: 1, mt: 1 }}>
                <Typography sx={{ color: '#dc2626', fontWeight: 'bold' }}>Amount Due</Typography>
                <Typography sx={{ color: '#dc2626', fontWeight: 'bold' }}>
                  ₹{amountDue.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ background: '#f1f5f9', p: 1.5, textAlign: 'right', borderTop: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                * Please ensure payment is completed before trip closure.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* All Transactions Table */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#3b82f6' }}>
            Transaction History
          </Typography>

          {loadingTransactions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} sx={{ color: '#3b82f6' }} />
            </Box>
          ) : transactions.length > 0 ? (
            <TableContainer component={Paper} sx={{ backgroundColor: '#1e293b', color: 'white' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', p: 1 }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', p: 1 }}>Type</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', p: 0.5 }}>Amount(₹)</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', p: 0.5 }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', p: 1 }}>Trn/Rzr pay id</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', p: 3 }}>Payment Info</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', p: 1 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell sx={{ color: 'white', p: 1 }}>
                        {(() => {
                          // Try to get date from transactionDate first
                          if (transaction.transactionDate) {
                            return new Date(transaction.transactionDate).toLocaleDateString('en-IN');
                          }

                          // Try to infer date from transaction creation patterns
                          // For refunds, try to use current date as fallback since they're processed immediately
                          if (transaction.transactionType === 'REFUND' || transaction.amount < 0) {
                            return <span style={{ color: '#fbbf24', fontStyle: 'italic' }}>Recent</span>;
                          }

                          // For other transactions, show date not available
                          return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Date not available</span>;
                        })()
                        }
                      </TableCell>
                      {/* <TableCell sx={{ color: 'white', p: 1 }}>
                        {(() => {
                          // Priority 1: Check if this is a customer-facing transaction with paymentMethod
                          if (transaction.paymentMode) {
                            // Customer transactions - show the actual payment method
                            return transaction.paymentMode;
                          }

                          // Priority 2: Handle operator-created transactions by transactionType
                          if (!transaction.transactionType) {
                            // Try to infer type from other fields
                            if (transaction.razorpayPaymentLinkId || transaction.razorpayPaymentLinkUrl) {
                              return 'Payment Link';
                            } else if (transaction.refNumber) {
                              return 'Cash Payment';
                            } else if (transaction.amount < 0) {
                              return 'Refund';
                            } else {
                              return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Unknown</span>;
                            }
                          }

                          // Priority 3: Map transactionType for operator-created transactions
                          switch (transaction.transactionType) {
                            case 'PAYMENT_LINK': return 'Payment Link';
                            case 'PAYMENT':
                              // For PAYMENT type, check if it has paymentMethod, otherwise default to Cash
                              return transaction.paymentMode || 'Cash Payment';
                            case 'CASH': return 'Cash Payment';
                            case 'REFUND': return 'Refund';
                            case 'CANCEL_FEE' : return 'Cancel Fee';
                            case 'REFUND': return 'Refund'
                            default: return transaction.transactionType;
                          }
                        })()
                        }
                      </TableCell> */}
                      <TableCell sx={{ color: 'white', p: 1 }}>
                        {(() => {
                          // Priority 1: Handle operator-created transactions by transactionType
                          if (transaction.transactionType) {
                            switch (transaction.transactionType) {
                              case 'PAYMENT_LINK':
                                return 'Payment Link';
                              case 'PAYMENT':
                                // For PAYMENT type, determine the sub-type based on paymentMode
                                if (transaction.paymentMode === 'CASH') {
                                  return 'CASH';
                                } else if (transaction.paymentMode === 'QRCODE') {
                                  return 'QRCODE';
                                } else if (transaction.paymentMode === 'DRQRCODE') {
                                  return 'DRQRCODE';
                                } else if (transaction.paymentMode) {
                                  // For other payment modes like OMINICARD, show as "Online Payment"
                                  return transaction.paymentMode;
                                }
                                return 'Payment';
                              case 'CASH':
                                return 'Cash Payment';
                              case 'REFUND':
                                return 'REFUND';
                              case 'CANCEL_FEE':
                                return 'CANCEL FEE';
                              default:
                                return transaction.transactionType;
                            }
                          }

                          // Priority 2: Fallback - Try to infer type from other fields
                          if (transaction.razorpayPaymentLinkId || transaction.razorpayPaymentLinkUrl) {
                            return 'Payment Link';
                          } else if (transaction.refNumber) {
                            return 'Cash Payment';
                          } else if (transaction.amount < 0) {
                            return 'Refund';
                          } else {
                            return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Unknown</span>;
                          }
                        })()}
                      </TableCell>
                      <TableCell sx={{ color: 'white', p: 1 }}>
                        {transaction.currency} {transaction.amount.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ color: 'white', p: 0.5 }}>
                        <Box sx={{
                          display: 'inline-block',
                          backgroundColor:
                            transaction.status === 'PAID' || transaction.status === 'SUCCESS' ? '#10b981' :
                              transaction.status === 'PENDING' ? '#f59e0b' :
                                transaction.status === 'REFUNDED' ? '#6b7280' : '#ef4444',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem'
                        }}>
                          {transaction.status}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'white', pl: 1 }}>
                        {(transaction.transactionType === 'PAYMENT_Link' || transaction.transactionType === 'CARD') ? transaction.razorpayPaymentLinkId : transaction.refNumber}
                      </TableCell>
                      <TableCell sx={{ color: 'white', pl: 4 }}>
                        {transaction.transactionType === 'PAYMENT_LINK' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {transaction.razorpayPaymentLinkUrl && transaction.status !== 'REFUNDED' && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  if (transaction.razorpayPaymentLinkUrl) {
                                    // Try using the Clipboard API with fallback
                                    try {
                                      navigator.clipboard.writeText(transaction.razorpayPaymentLinkUrl)
                                        .then(() => {
                                          setSnackbarMessage('Payment link copied to clipboard!');
                                          setSnackbarSeverity('success');
                                          setSnackbarOpen(true);
                                        })
                                        .catch((error) => {
                                          console.error('Clipboard API failed:', error);
                                          // Fallback: Create a temporary input element for copying
                                          const textArea = document.createElement('textarea');
                                          textArea.value = transaction.razorpayPaymentLinkUrl || '';
                                          textArea.style.position = 'fixed';  // Avoid scrolling to bottom
                                          document.body.appendChild(textArea);
                                          textArea.focus();
                                          textArea.select();

                                          try {
                                            const successful = document.execCommand('copy');
                                            if (successful) {
                                              setSnackbarMessage('Payment link copied to clipboard!');
                                              setSnackbarSeverity('success');
                                            } else {
                                              setSnackbarMessage('Could not copy link. Please try manually copying the link.');
                                              setSnackbarSeverity('error');
                                            }
                                          } catch (err) {
                                            console.error('Fallback clipboard method failed:', err);
                                            setSnackbarMessage('Could not copy link. Please ensure you are using HTTPS.');
                                            setSnackbarSeverity('error');
                                          }

                                          document.body.removeChild(textArea);
                                          setSnackbarOpen(true);
                                        });
                                    } catch (error) {
                                      console.error('Clipboard API not available:', error);
                                      // Direct fallback if Clipboard API is not available at all
                                      try {
                                        const textArea = document.createElement('textarea');
                                        textArea.value = transaction.razorpayPaymentLinkUrl || '';
                                        textArea.style.position = 'fixed';
                                        document.body.appendChild(textArea);
                                        textArea.focus();
                                        textArea.select();

                                        const successful = document.execCommand('copy');
                                        if (successful) {
                                          setSnackbarMessage('Payment link copied to clipboard!');
                                          setSnackbarSeverity('success');
                                        } else {
                                          setSnackbarMessage('Could not copy link. Please try manually copying the link.');
                                          setSnackbarSeverity('error');
                                        }
                                        document.body.removeChild(textArea);
                                      } catch (err) {
                                        console.error('Fallback clipboard method failed:', err);
                                        setSnackbarMessage('Could not copy link. Please ensure you are using HTTPS.');
                                        setSnackbarSeverity('error');
                                      }
                                      setSnackbarOpen(true);
                                    }
                                  }
                                }}
                                disabled={disabled || isInCancellation}
                                sx={{ color: '#3b82f6', borderColor: '#3b82f6', fontSize: '0.7rem' }}
                              >
                                Copy Link
                              </Button>
                            )}
                            {(transaction.status === 'PENDING' || transaction.status === 'created') && transaction.razorpayPaymentLinkId && (
                              <>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleCheckPaymentStatus(transaction)}
                                  disabled={disabled}
                                  sx={{ color: '#10b981', borderColor: '#10b981', fontSize: '0.7rem' }}
                                >
                                  Refresh Status
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<WhatsAppIcon />}
                                  onClick={() => handleOpenPaymentLinkWhatsAppDialog(transaction)}
                                  sx={{
                                    color: '#25D366',
                                    borderColor: '#25D366',
                                    fontSize: '0.7rem',
                                    ml: 1,
                                    '&:hover': { backgroundColor: 'rgba(37, 211, 102, 0.1)' }
                                  }}
                                  disabled={!initialCustomerMobile || disabled || isInCancellation}
                                >
                                  Send Payment Link
                                </Button>
                              </>
                            )}
                            {transaction.transactionType === 'PAYMENT_LINK' && transaction.status === 'SUCCESS' && (
                              <>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<WhatsAppIcon />}
                                  onClick={() => handleOpenWhatsAppDialog(transaction)}
                                  sx={{
                                    color: '#25D366',
                                    borderColor: '#25D366',
                                    fontSize: '0.7rem',
                                    ml: 1,
                                    '&:hover': { backgroundColor: 'rgba(37, 211, 102, 0.1)' }
                                  }}
                                  disabled={!initialCustomerMobile || disabled || isInCancellation}
                                >
                                  Send Payment Received
                                </Button>
                              </>
                            )}
                          </Box>
                        ) : transaction.transactionType === 'PAYMENT' && transaction.status === 'SUCCESS' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<WhatsAppIcon />}
                              onClick={() => handleOpenWhatsAppDialog(transaction)}
                              sx={{
                                color: '#25D366',
                                borderColor: '#25D366',
                                fontSize: '0.7rem',
                                '&:hover': { backgroundColor: 'rgba(37, 211, 102, 0.1)' }
                              }}
                              disabled={!initialCustomerMobile || disabled || isInCancellation}
                            >
                              Send Payment Received
                            </Button>
                            {transaction.paymentMode === 'CASH' && (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {userRole === 'OPERATOR' && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={disabled || (userRole === 'OPERATOR' && transaction.cashInHand === 'ADMIN' || transaction.cashInHand === 'OPERATOR' || transaction.cashInHand === 'BANK' || transaction.cashInHand === 'AUDITED' || transaction.cashInHand === null)}
                                    onClick={() => { handleCashHandoverDialog(transaction.id) }}
                                    sx={{ color: transaction.cashInHand === 'OPERATOR' ? 'green' : '#3b82f6', borderColor: transaction.cashInHand === 'OPERATOR' ? 'green' : '#3b82f6', fontSize: '0.7rem' }}
                                  >
                                    {transaction.cashInHand === 'OPERATOR'
                                      ? "Cash with Operator"
                                      : transaction.cashInHand === 'ADMIN'
                                        ? "Cash with Admin" : transaction.cashInHand === 'DRIVER' ?
                                          'set Cash to Operator'
                                          : transaction.cashInHand
                                            ? `Cash with ${transaction.cashInHand.charAt(0) + transaction.cashInHand.slice(1).toLowerCase()}` : "cash status unknown"}
                                  </Button>)}
                                {userRole === 'ADMIN' && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={disabled || (userRole === 'ADMIN' && (transaction.cashInHand === 'DRIVER' || transaction.cashInHand === 'ADMIN' || transaction.cashInHand === 'BANK' || transaction.cashInHand === 'AUDITED' || transaction.cashInHand === 'null'))}
                                    onClick={() => { handleCashHandoverDialog(transaction.id) }}
                                    sx={{ color: transaction.cashInHand === 'ADMIN' ? 'green' : '#3b82f6', borderColor: transaction.cashInHand === 'ADMIN' ? 'green' : '#3b82f6', fontSize: '0.7rem' }}>
                                    {transaction.cashInHand === 'ADMIN'
                                      ? "Cash with Admin"
                                      : transaction.cashInHand === 'OPERATOR'
                                        ? "Set Cash to Admin" :
                                        transaction.cashInHand === 'DRIVER'
                                          ? "Cash with Driver" : transaction.cashInHand
                                            ? `Cash with ${transaction.cashInHand.charAt(0) + transaction.cashInHand.slice(1).toLowerCase()}` : "cash status unknown"}
                                  </Button>)}

                                {userRole === 'ACCOUNTANT' && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={true}
                                    sx={{ color: transaction.cashInHand === 'ADMIN' || transaction.cashInHand === 'OPERATOR' ? 'green' : '#3b82f6', borderColor: transaction.cashInHand === 'ADMIN' || transaction.cashInHand === 'OPERATOR' ? 'green' : '#3b82f6', fontSize: '0.7rem' }}
                                  >
                                    {transaction.cashInHand === 'OPERATOR'
                                      ? "Cash with Operator"
                                      : transaction.cashInHand === 'ADMIN'
                                        ? "Cash with Admin"
                                        : transaction.cashInHand === 'DRIVER'
                                          ? "Cash with Driver"
                                          : transaction.cashInHand
                                            ? `Cash with ${transaction.cashInHand.charAt(0) + transaction.cashInHand.slice(1).toLowerCase()}`
                                            : "Cash status unknown"}
                                  </Button>)}
                              </Box>
                            )}
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ color: 'white', p: 0.5 }}>
                        {transaction.paymentMode === 'CASH' && transaction.cashInHand === 'DRIVER' &&
                          <Button
                            variant="outlined"
                            disabled={disabled || isInCancellation}
                            size="small"
                            onClick={() => { handleOpenModifyAmountDialog(transaction); }}>
                            Modify Amt.
                          </Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed #555' }}>
              <Typography variant="body1" color="#9ca3af">
                No transaction history available
              </Typography>
            </Box>
          )}
        </Box>

        {/* Add New Payment */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2, color: '#3b82f6' }}>
            Add New Payment
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
              Amount
            </Typography>
            <TextField
              fullWidth
              value={paymentAmount}
              onChange={(e) => {
                const value = e.target.value;
                const numValue = parseFloat(value);

                // Only update if it's a valid number and not more than amount due
                if (value === '' || (isFinite(numValue) && numValue > 0 && numValue <= amountDue)) {
                  setPaymentAmount(value);
                } else if (numValue > amountDue) {
                  // If amount is greater than due, set to max due amount
                  setPaymentAmount(amountDue.toString());
                  setSnackbarMessage('Amount cannot exceed the due amount');
                  setSnackbarSeverity('error');
                  setSnackbarOpen(true);
                }
              }}
              placeholder="₹"
              variant="outlined"
              size="small"
              disabled={disabled}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#333',
                  color: 'white',
                  '& fieldset': { borderColor: '#555' },
                  '&:hover fieldset': { borderColor: '#777' },
                  '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                },
                '& .MuiInputBase-input': { color: 'white' }
              }}
              helperText={disabled ? 'Cannot add payment - disabled for current status' : `Maximum amount: ₹${amountDue.toFixed(2)}`}
              FormHelperTextProps={{ sx: { color: disabled ? '#ef4444' : '#9ca3af' } }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              disabled={isProcessing || paymentAmount === '' || disabled || isInCancellation}
              onClick={() => handleOpenCashPaymentDialog('CASH')}
              sx={{
                backgroundColor: '#6b7280',
                '&:hover': { backgroundColor: '#4b5563' }
              }}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'CASH PAYMENT'}
            </Button>
            <Button
              variant="contained"
              disabled={isProcessing || paymentAmount === '' || disabled || isInCancellation}
              onClick={() => handleOpenCashPaymentDialog('QRCODE')}
              sx={{
                backgroundColor: '#6b7280',
                '&:hover': { backgroundColor: '#4b5563' }
              }}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'QR PAYMENT'}
            </Button>
            <Button
              variant="contained"
              disabled={isProcessing || paymentAmount === '' || disabled || isInCancellation}
              onClick={() => { handleOpenCashPaymentDialog('DRQRCODE') }}
              sx={{
                backgroundColor: '#6b7280',
                '&:hover': { backgroundColor: '#4b5563' }
              }}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'DRIVER QR PAYMENT'}
            </Button>
            <Button
              variant="contained"
              disabled={isProcessing || paymentAmount === '' || disabled || isInCancellation}
              onClick={() => handleOpenPaymentLinkDialog()}
              sx={{
                backgroundColor: '#6b7280',
                '&:hover': { backgroundColor: '#4b5563' }
              }}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'CREATE PAYMENT LINK'}
            </Button>
            <Button
              variant="contained"
              disabled={disabled || !isInCancellation}
              onClick={() => setOpenRefundDialog(true)}
              sx={{
                backgroundColor: '#6b7280',
                '&:hover': { backgroundColor: '#4b5563' }
              }}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'REFUND'}
            </Button>
          </Box>
        </Box>
      </CardContent>

      {/*  modify Amount dialog  */}
      <Dialog open={openModifyAmountDialog} onClose={handleCloseModifyAmountDialog}>
        <DialogTitle sx={{ pb: 2 }}>Modify Cash Amount</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Amount (₹)"
            type="text"
            size="small"
            fullWidth
            error={!!modifyAmountError}
            helperText={modifyAmountError}
            variant="outlined"
            value={modifyAmount}
            onChange={(e) => {
              const value = e.target.value;
              if (validateModifyAmountInput(value)) {
                setModifyAmount(value);
                setModifyAmountError('');
              }
            }}
            disabled={disabled}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#333',
                color: 'white',
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#777' },
                '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
              },
              '& .MuiInputLabel-root': { color: '#9ca3af' },
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiFormHelperText-root': { color: '#ef4444' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={handleCloseModifyAmountDialog}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => { handleSaveModifyAmount() }}
            disabled={isProcessing || !modifyAmount || disabled}
          >
            {isProcessing ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Link Dialog */}
      <Dialog open={openPaymentLinkDialog} onClose={handleClosePaymentLinkDialog}>
        <DialogTitle>Create Payment Link</DialogTitle>
        <DialogContent>
          {!paymentLink ? (
            <>
              <DialogContentText>
                Create a payment link for ₹{paymentAmount} that can be shared with the customer.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label="Customer Name"
                type="text"
                fullWidth
                variant="outlined"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                disabled={disabled}
              />
              <TextField
                margin="dense"
                label="Customer Mobile"
                type="text"
                fullWidth
                variant="outlined"
                value={customerMobile}
                onChange={(e) => setCustomerMobile(e.target.value)}
                disabled={disabled}
              />
              <TextField
                margin="dense"
                label="Customer Email (Optional)"
                type="email"
                fullWidth
                variant="outlined"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                disabled={disabled}
              />
            </>
          ) : (
            <>
              <DialogContentText>
                Payment link created successfully! Share this link with the customer:
              </DialogContentText>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                  {paymentLink}
                </Typography>
                <Button
                  startIcon={<ContentCopy />}
                  onClick={handleCopyPaymentLink}
                  size="small"
                >
                  Copy
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentLinkDialog}>Close</Button>
          {!paymentLink && (
            <Button
              onClick={handleCreatePaymentLink}
              disabled={isProcessing || !customerName || !customerMobile || disabled}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cash Payment Dialog */}
      <Dialog open={openCashPaymentDialog} onClose={handleCloseCashPaymentDialog}>
        <DialogTitle>Process {paymentMode === 'CASH' ? 'Cash' : 'QR Code'} Payment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Record a payment of ₹{paymentAmount} for this trip.
          </DialogContentText>
          {paymentMode === 'CASH' && (<TextField
            margin="dense"
            label="Driver"
            type="text"
            select
            fullWidth
            variant="outlined"
            value={selectedDriverId || ""}
            onChange={(e) => {
              setSelectedDriverId(Number(e.target.value));
              if (validationErrors.driverId) {
                setValidationErrors(prev => ({ ...prev, driverId: undefined }));
              }
            }}
            disabled={false}
            error={!!validationErrors.driverId}
            helperText={validationErrors.driverId}
          >
            {drivers.map((driver: any) => (<MenuItem key={driver.id} value={driver.id}>
              {`${driver.firstName} ${driver.lastName}`}
            </MenuItem>))}
          </TextField>)}

          <TextField
            autoFocus
            margin="dense"
            label="Customer Name"
            type="text"
            fullWidth
            variant="outlined"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            disabled={disabled || !!customerName}
            InputProps={{ readOnly: true }}
          />
          <TextField
            margin="dense"
            label="Customer Mobile"
            type="text"
            fullWidth
            variant="outlined"
            value={customerMobile}
            onChange={(e) => setCustomerMobile(e.target.value)}
            disabled={disabled || !!customerMobile}
            InputProps={{ readOnly: true }}
          />
          <TextField
            margin="dense"
            label={paymentMode === 'CASH' ? "Receipt Number (Optional)" : "Payment ID"}
            type="text"
            fullWidth
            variant="outlined"
            value={refNumber}
            onChange={(e) => {
              setRefNumber(e.target.value);
              // Clear error when user starts typing
              if (validationErrors.refNumber) {
                setValidationErrors(prev => ({ ...prev, refNumber: undefined }));
              }
            }}
            disabled={disabled}
            error={!!validationErrors.refNumber}
            // helperText={validationErrors.refNumber || (paymentMode === 'QRCODE' ? 'Required for QR Code payments' : '')}
            required={paymentMode !== 'CASH'}
          />
          <TextField
            margin="dense"
            label="Notes (Optional)"
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value)}
            disabled={disabled}
          />
        </DialogContent>
        <DialogActions>
          <Button sx={{ backgroundColor: '#f3f4f6', color: '#1e293b', '&:hover': { backgroundColor: '#e5e7eb' } }} onClick={handleCloseCashPaymentDialog}>Cancel</Button>
          <Button
            onClick={handleCashPayment}
            disabled={isProcessing || disabled}
            sx={{ backgroundColor: '#10b981', color: 'white', '&:hover': { backgroundColor: '#059669' } }}
          >
            {isProcessing ? <CircularProgress size={24} /> : 'Process Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Payment Received Notification Dialog */}
      <Dialog open={openWhatsAppDialog} onClose={handleCloseWhatsAppDialog}>
        <DialogTitle sx={{ bgcolor: '#075E54', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon /> Send Payment Confirmation
        </DialogTitle>
        <DialogContent sx={{ mt: 2, bgcolor: '#1e293b', color: 'white' }}>
          <DialogContentText sx={{ color: '#e2e8f0' }}>
            Send a WhatsApp notification to confirm payment received for this trip.
          </DialogContentText>

          {selectedPaymentTransaction && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#2d3748', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Payment Details:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Customer:</Typography>
                <Typography variant="body2">{initialCustomerName || 'N/A'}</Typography>

                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Mobile:</Typography>
                <Typography variant="body2">{initialCustomerMobile || 'N/A'}</Typography>

                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Amount:</Typography>
                <Typography variant="body2">₹{selectedPaymentTransaction.amount.toString()}</Typography>

                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Date:</Typography>
                <Typography variant="body2">
                  {selectedPaymentTransaction.transactionDate ?
                    new Date(selectedPaymentTransaction.transactionDate).toLocaleDateString() : 'N/A'}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Trip ID:</Typography>
                <Typography variant="body2">{tripId || 'N/A'}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWhatsAppDialog} sx={{ color: 'white' }}>Cancel</Button>
          <Button
            onClick={handleSendWhatsAppNotification}
            disabled={sendingNotification || !initialCustomerMobile || disabled || isInCancellation}
            startIcon={<WhatsAppIcon />}
            sx={{
              bgcolor: '#25D366',
              color: 'white',
              '&:hover': { bgcolor: '#128C7E' },
              '&.Mui-disabled': { bgcolor: '#cccccc' }
            }}
          >
            {sendingNotification ? <CircularProgress size={24} /> : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Payment Link Notification Dialog */}
      <Dialog open={openPaymentLinkWhatsAppDialog} onClose={handleClosePaymentLinkWhatsAppDialog}>
        <DialogTitle sx={{ bgcolor: '#075E54', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon /> Send Payment Link
        </DialogTitle>
        <DialogContent sx={{ mt: 2, bgcolor: '#1e293b', color: 'white' }}>
          <DialogContentText sx={{ color: '#e2e8f0' }}>
            Send a WhatsApp notification with the payment link for this trip.
          </DialogContentText>

          {selectedPaymentLinkTransaction && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#2d3748', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Payment Link Details:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Customer:</Typography>
                <Typography variant="body2">{initialCustomerName || 'N/A'}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Mobile:</Typography>
                <Typography variant="body2">{initialCustomerMobile || 'N/A'}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Amount:</Typography>
                <Typography variant="body2">₹{selectedPaymentLinkTransaction.amount.toFixed(2)}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Payment Link:</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {selectedPaymentLinkTransaction.razorpayPaymentLinkUrl || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Trip ID:</Typography>
                <Typography variant="body2">{tripId || 'N/A'}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentLinkWhatsAppDialog} sx={{ color: 'white' }}>Cancel</Button>
          <Button
            onClick={handleSendPaymentLinkWhatsAppNotification}
            disabled={sendingNotification || !initialCustomerMobile || disabled || isInCancellation}
            startIcon={<WhatsAppIcon />}
            sx={{
              bgcolor: '#25D366',
              color: 'white',
              '&:hover': { bgcolor: '#128C7E' },
              '&.Mui-disabled': { bgcolor: '#cccccc' }
            }}
          >
            {sendingNotification ? <CircularProgress size={24} /> : 'Send Payment Link'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* set cash to operator dialog */}
      <Dialog open={openCashHandoverDialog} onClose={() => { }}>
        <DialogTitle sx={{ backgroundColor: 'white', color: '#ef4444' }}>Cash to Operator</DialogTitle>
        <DialogContent sx={{ backgroundColor: 'white' }}>
          {userRole === 'OPERATOR' && (<DialogContentText sx={{ color: '#ef4444' }}>
            Please confirm that Operator received the cash.<br />
            Are you sure you want to proceed?
          </DialogContentText>)}
          {userRole === 'ADMIN' && (<DialogContentText sx={{ color: '#ef4444' }}>
            Please confirm that Admin received the cash.<br />
            Are you sure you want to proceed?
          </DialogContentText>)}
          <DialogActions sx={{ p: 2, backgroundColor: 'white' }}>
            <Button
              onClick={() => { setOpenCashHandoverDialog(false); setSelectedCashTransactionId(null); }}
              sx={{ backgroundColor: '#f3f4f6', color: '#1e293b', '&:hover': { backgroundColor: '#e5e7eb' } }}
            >
              No
            </Button>
            <Button
              onClick={() => handleSetCashToUser(userRole === 'ADMIN' ? 'ADMIN' : 'OPERATOR')}
              variant="contained"
              sx={{ backgroundColor: '#10b981', color: 'white', '&:hover': { backgroundColor: '#059669' } }}
            >
              Yes
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      {/* omnicard refund dialog */}

      <Dialog open={openRefundDialog} onClose={() => setOpenRefundDialog(false)}>
        <DialogTitle>Refund Payment</DialogTitle>
        {/* <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to refund this payment?
        </Typography> */}
        {/* <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Typography variant="body2">Customer Name:</Typography>
          <Typography variant="body2">{customerName}</Typography>

          <Typography variant="body2">Customer Mobile:</Typography>
          <Typography variant="body2">{customerMobile}</Typography>
        </Box> */}
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Refund Amount"
            size="small"
            type="text"
            fullWidth
            variant="outlined"
            value={refundAmount}
            onChange={(e) => {
              setRefundAmount(e.target.value);
              setRefundValidationErrors(prev => ({ ...prev, refundAmount: undefined }));
            }}
            error={!!refundValidationErrors.refundAmount}
            helperText={refundValidationErrors.refundAmount || `Maximum refundable: ₹${totalPaid.toFixed(2)}`}
          />
          <TextField
            autoFocus
            margin="dense"
            label="Cancellation Charges (if any)"
            size="small"
            type="text"
            fullWidth
            variant="outlined"
            value={cancellationFee}
            onChange={(e) => {
              setCancellationFee(e.target.value);
              setRefundValidationErrors(prev => ({ ...prev, cancellationFee: undefined }));
            }}
            error={!!refundValidationErrors.cancellationFee}
            helperText={refundValidationErrors.cancellationFee}
          />
          <TextField
            fullWidth
            select
            label="Reason for Refund"
            value={refundReason}
            onChange={(e) => {
              setRefundReason(e.target.value);
              setRefundValidationErrors(prev => ({ ...prev, refundReason: undefined }));
            }}
            margin="normal"
            variant="outlined"
            disabled={disabled}
            error={!!refundValidationErrors.refundReason}
            helperText={refundValidationErrors.refundReason}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#333',
                color: 'white',
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#777' },
                '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
              },
              '& .MuiInputLabel-root': { color: '#9ca3af' },
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiSvgIcon-root': { color: 'white' }
            }}
          >
            <MenuItem value="requested_by_customer">Requested by Customer</MenuItem>
            <MenuItem value="duplicate">Duplicate Payment</MenuItem>
            <MenuItem value="fraudulent">Fraudulent Transaction</MenuItem>
            <MenuItem value="service_not_provided">Service Not Provided</MenuItem>
          </TextField>
          <TextField
            autoFocus
            margin="dense"
            label="Transaction Id"
            size="small"
            type="text"
            fullWidth
            variant="outlined"
            value={refundTransactionId}
            onChange={(e) => {
              setRefundTransactionId(e.target.value);
              setRefundValidationErrors(prev => ({ ...prev, refundTransactionId: undefined }));
            }}
            error={!!refundValidationErrors.refundTransactionId}
            helperText={refundValidationErrors.refundTransactionId}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setOpenRefundDialog(false);
              setSelectedModifyTransaction(null);
              setRefundAmount('');
              setRefundValidationErrors({});
              setCancellationFee('');
              setRefundReason('');
              setRefundTransactionId('');
            }}
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleOminiRefund}
            variant="contained"
            sx={{ backgroundColor: '#10b981', color: 'white', '&:hover': { backgroundColor: '#059669' } }}
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={24} /> : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};



export default TripPricingSection;
